import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LangContext';
import { EmailNotification, PaginationParams } from '../types';
import { getEmailNotifications, getEmailNotificationById } from '../api/emailNotificationsApi';
import toast from 'react-hot-toast';

export const EmailList: React.FC = () => {
  const { t } = useLang();
  const [emails, setEmails] = useState<EmailNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailNotification | null>(null);
  const [showEmailView, setShowEmailView] = useState(false);
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEmails();
  }, [pagination.page, pagination.limit, searchTerm]);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
      };
      
      const response = await getEmailNotifications(params);
      setEmails(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.total,
        totalPages: response.totalPages
      }));
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Không thể tải danh sách email');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClick = async (emailId: string) => {
    try {
      const email = await getEmailNotificationById(emailId);
      setSelectedEmail(email);
      setShowEmailView(true);
    } catch (error) {
      console.error('Error loading email details:', error);
      toast.error(t('errorLoadingEmailDetails'));
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'SENT': { label: t('emailSent'), color: 'bg-green-100 text-green-800' },
      'SENDING': { label: t('emailSending'), color: 'bg-yellow-100 text-yellow-800' },
      'FAILED': { label: t('emailFailed'), color: 'bg-red-100 text-red-800' },
      'DRAFT': { label: t('emailDraft'), color: 'bg-gray-100 text-gray-800' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || statusMap.DRAFT;
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (showEmailView && selectedEmail) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        {/* Breadcrumb */}
        <nav className="flex mb-6 text-sm">
          <span className="text-gray-500">🏠</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">Email</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-cyan-600 font-medium">{t('emailList')}</span>
        </nav>

        {/* Back Button */}
        <button
          onClick={() => setShowEmailView(false)}
          className="mb-4 flex items-center text-cyan-600 hover:text-cyan-700"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('backToEmailList')}
        </button>

        {/* Email View */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Email Header */}
          <div className="border-l-4 border-cyan-500 pl-4 mb-6">
            <div className="text-sm text-gray-600 mb-1">
              Tiêu đề: <span className="font-medium text-gray-900">{selectedEmail.subject}</span>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              Ngày gửi: <span className="font-medium">{formatDate(selectedEmail.sentAt || selectedEmail.createdAt)}</span>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              Gửi tới: <span className="font-medium">{selectedEmail.building.name} {selectedEmail.wasSentToAll ? '(Tất cả căn hộ)' : (selectedEmail.apartmentGroup ? `(${selectedEmail.apartmentGroup.name})` : '(Không xác định)')}</span>
            </div>
            {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
              <div className="text-sm text-gray-600">
                File đính kèm: {selectedEmail.attachments.map(att => att.originalName).join('; ')}
              </div>
            )}
          </div>

          {/* Email Content */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Kính gửi: Quý Cư dân Khu Căn hộ {selectedEmail.building.name}</p>
            <div 
              className="text-sm text-gray-700 leading-relaxed prose max-w-none email-content"
              dangerouslySetInnerHTML={{ __html: selectedEmail.content }}
            />
          </div>

          {/* Email Footer */}
          <div className="text-sm text-gray-600">
            <p>Trân trọng cảm ơn!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">Email</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">{t('emailList')}</span>
      </nav>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('emailList')}</h1>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Records per page */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700">{t('emailRecordsPerPage')}</label>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700">{t('emailSearchPlaceholder')}:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('emailSearchPlaceholder')}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        </div>
      </div>

      {/* Email Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cyan-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('emailTitle')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('emailSendDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('emailRecipientGroup')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('emailStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('emailSendCount')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    {t('loading')}
                  </td>
                </tr>
              ) : emails.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    {t('noEmail')}
                  </td>
                </tr>
              ) : (
                emails.map((email, index) => (
                  <tr 
                    key={email.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleEmailClick(email.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-cyan-600 hover:text-cyan-700">
                        {email.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(email.sentAt || email.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.building.name} {email.wasSentToAll ? `(${t('allApartments')})` : (email.apartmentGroup ? `(${email.apartmentGroup.name})` : `(${t('unknownGroup')})`)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(email.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.sentCount}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {t('viewing')} {Math.min(pagination.page * pagination.limit, pagination.total)} {t('of')} {pagination.total} {t('records')}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('previous')}
                </button>
                
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        pagination.page === pageNum
                          ? 'bg-cyan-500 text-white border-cyan-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('next')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 